import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createRole, RbacRole, roleKeys, RolePermission, updateRole } from "api/rbacRoles";
import {
  Alert,
  Badge,
  Button,
  FormField,
  Input,
  notify,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  Textarea,
} from "Components/ui";
import { RBAC_RESOURCES } from "config/rbacCatalog";
import RolePermissionForm from "./RolePermissionForm";
import {
  actionsOf,
  countGranted,
  isFullAccessRole,
  setGrants,
  toPermissionRows,
  TOTAL_PERMISSIONS,
} from "./permissions";

export type RoleEditorState =
  | { mode: "create" }
  | { mode: "edit"; role: RbacRole }
  | { mode: "view"; role: RbacRole }
  | null;

interface RoleEditorSheetProps {
  state: RoleEditorState;
  onClose: () => void;
}

const MAX_NAME = 60;

const fingerprint = (name: string, description: string, rows: RolePermission[]) =>
  JSON.stringify({
    name: name.trim(),
    description: description.trim(),
    rows: rows.map((row) => JSON.stringify(row, Object.keys(row).sort())).sort(),
  });

/** Create, edit or read one role in a side panel: name, description and the permission grid. */
const RoleEditorSheet: React.FC<RoleEditorSheetProps> = ({ state, onClose }) => {
  const queryClient = useQueryClient();
  const role = state && state.mode !== "create" ? state.role : null;
  const fullAccess = Boolean(role && isFullAccessRole(role));
  // Saving a full-access role would resend its permissions, and for legacy roles the server
  // recomputes full access from them; it is never edited here.
  const readOnly = state?.mode === "view" || fullAccess;

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [rows, setRows] = React.useState<RolePermission[]>([]);
  const [initial, setInitial] = React.useState("");
  const [showErrors, setShowErrors] = React.useState(false);

  React.useEffect(() => {
    if (!state) {
      return;
    }
    const nextName = role ? role.name : "";
    const nextDescription = role?.description || "";
    const nextRows = role ? toPermissionRows(role.permissions) : [];
    setName(nextName);
    setDescription(nextDescription);
    setRows(nextRows);
    setInitial(fingerprint(nextName, nextDescription, nextRows));
    setShowErrors(false);
  }, [state, role]);

  const save = useMutation({
    mutationFn: () => {
      const input = { name: name.trim(), description: description.trim(), permissions: rows };
      return role ? updateRole(role._id, input) : createRole(input);
    },
    onSuccess: () => {
      notify.success(role ? "Role saved" : "Role created");
      queryClient.invalidateQueries({ queryKey: roleKeys.all });
      onClose();
    },
    onError: (err) => notify.error(err, { fallback: "The role wasn't saved." }),
  });

  const nameError = !name.trim()
    ? "Give the role a name."
    : name.trim().length > MAX_NAME
    ? `Keep the name under ${MAX_NAME} characters.`
    : null;
  const dirty = fingerprint(name, description, rows) !== initial;
  const granted = countGranted(rows);

  const handleSave = () => {
    setShowErrors(true);
    if (!nameError) {
      save.mutate();
    }
  };

  const viewEverything = () =>
    setRows((current) =>
      RBAC_RESOURCES.reduce(
        (acc, resource) => setGrants(acc, resource.resource, ["read"], true),
        current
      )
    );
  const clearAll = () =>
    setRows((current) =>
      RBAC_RESOURCES.reduce(
        (acc, resource) => setGrants(acc, resource.resource, actionsOf(resource), false),
        current
      )
    );

  const title = !state
    ? ""
    : state.mode === "create"
    ? "New role"
    : state.mode === "view"
    ? state.role.name
    : `Edit ${state.role.name}`;

  return (
    <Sheet open={Boolean(state)} onOpenChange={(open) => (open ? undefined : onClose())}>
      <SheetContent size="lg">
        <SheetHeader
          title={title}
          description={
            fullAccess
              ? "Super Admin can't be edited."
              : readOnly
              ? "You can view this role. Changing it needs role edit access."
              : "Name the role and tick what it may do. People get roles on the Users page."
          }
        />
        <SheetBody className="space-y-6">
          <div className="space-y-4">
            <FormField label="Name" required error={showErrors ? nameError : undefined}>
              <Input
                value={name}
                maxLength={MAX_NAME + 20}
                disabled={readOnly}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Billing Manager"
              />
            </FormField>
            <FormField label="Description" hint="Optional. Who this role is for.">
              <Textarea
                rows={2}
                value={description}
                disabled={readOnly}
                onChange={(event) => setDescription(event.target.value)}
              />
            </FormField>
          </div>

          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">Permissions</h3>
                {!fullAccess && (
                  <Badge>
                    {granted} of {TOTAL_PERMISSIONS}
                  </Badge>
                )}
              </div>
              {!readOnly && (
                <div className="flex gap-2">
                  <Button variant="ghost" size="xs" onClick={viewEverything}>
                    View everything
                  </Button>
                  <Button variant="ghost" size="xs" onClick={clearAll} disabled={granted === 0}>
                    Clear all
                  </Button>
                </div>
              )}
            </div>
            {fullAccess ? (
              <Alert tone="info" title="Full access">
                This role can do everything in SAP, including anything added later, so it has no
                permission grid.
              </Alert>
            ) : (
              <RolePermissionForm value={rows} onChange={setRows} disabled={readOnly} />
            )}
          </section>
        </SheetBody>
        <SheetFooter>
          {readOnly ? (
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={onClose} disabled={save.isLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                loading={save.isLoading}
                disabled={state?.mode === "edit" && !dirty}
              >
                {state?.mode === "create" ? "Create role" : "Save changes"}
              </Button>
            </>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default RoleEditorSheet;
